import { gql, useQuery } from '@apollo/client';
import { Button, Code, Heading } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { ScrollToMe } from 'src/components/common/scrollToMe/ScrollToMe';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphqlCodeBlock } from 'src/components/graphqlCodeBlock/GraphqlCodeBlock';
import { GraphQLDiffView } from 'src/components/graphQLDiffView/GraphQLDiffView';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { SummaryItem } from '../../proposalLayout/DiffSummary';

import { LoadingSkeleton } from './LoadingSkeleton';

export function SchemaDiff({
  graphId,
  linkedSdl,
  schemaName,
  schemaSlug,
  baseHash,
  proposedHash,
}: {
  graphId: string;
  linkedSdl: string | undefined;
  schemaName: string;
  schemaSlug: string;
  baseHash: string | null;
  proposedHash: string | null;
}) {
  const isUnchanged = baseHash === proposedHash;
  const [showSdl, setShowSdl] = useState(
    !isUnchanged && schemaName !== 'Supergraph',
  );
  const [inViewRef, inView] = useInView();
  const shouldFetchSdl = useRef(inView && showSdl);
  shouldFetchSdl.current ||= inView && showSdl;
  const { data, loading, error } = useQuery<
    GraphQLTypes.SchemaDiffQuery,
    GraphQLTypes.SchemaDiffQueryVariables
  >(
    gql`
      query SchemaDiffQuery(
        $graphId: ID!
        $baseHash: SHA256
        $proposedHash: SHA256
        $isUnchanged: Boolean!
      ) {
        service(id: $graphId) {
          id
          baseDoc: doc(hash: $baseHash) {
            source
          }
          proposedDoc: doc(hash: $proposedHash) @skip(if: $isUnchanged) {
            source
          }
        }
      }
    `,
    {
      skip: !shouldFetchSdl.current,
      variables: { graphId, baseHash, proposedHash, isUnchanged },
    },
  );

  const shouldRenderRef = useRef(inView && !loading);
  shouldRenderRef.current ||= inView && !loading;

  const [diffStats, setDiffStats] = useState<{
    added: number;
    removed: number;
  } | null>(null);
  return (
    <div className="pb-4" ref={inViewRef}>
      {linkedSdl === schemaSlug && <ScrollToMe />}
      <div className="flex items-end pb-3">
        <Heading size="xl">{schemaName}</Heading>
        {diffStats && (
          <>
            <SummaryItem
              action="added"
              count={diffStats.added}
              className="text-code pl-2"
            />
            <SummaryItem
              action="removed"
              count={diffStats.removed}
              className="text-code pl-2"
            />
          </>
        )}
      </div>
      <div className="rounded border border-primary">
        <div className="flex border-b border-primary">
          <div
            className={classnames('text-body-sb p-3', {
              'w-1/2': !isUnchanged,
            })}
          >
            Base{' '}
            {baseHash && (
              <Code canCopy={true} className="ml-2 max-w-28 truncate">
                {baseHash}
              </Code>
            )}
          </div>
          {!isUnchanged && (
            <div className="text-body-sb w-1/2 border-l border-primary p-3">
              Proposed{' '}
              {proposedHash && (
                <Code canCopy={true} className="ml-2 max-w-28 truncate">
                  {proposedHash}
                </Code>
              )}
            </div>
          )}
        </div>
        {loading || !shouldRenderRef.current ? (
          <div className="flex">
            <LoadingSkeleton
              className={classnames('py-2', isUnchanged ? 'flex-1' : 'w-1/2')}
            />
            {!isUnchanged && (
              <LoadingSkeleton className="w-1/2 border-l border-primary py-2" />
            )}
          </div>
        ) : error ? (
          <div className="m-2">
            <ErrorMessage error={error} />
          </div>
        ) : showSdl ? (
          isUnchanged ? (
            data?.service?.baseDoc?.source && (
              <GraphqlCodeBlock
                sdl={data.service.baseDoc.source}
                className="px-3 py-2 text-xs"
              />
            )
          ) : (
            <GraphQLDiffView
              oldSdl={data?.service?.baseDoc?.source}
              newSdl={data?.service?.proposedDoc?.source}
              onDiffStats={setDiffStats}
              lineVisibilityType="only-changes"
              lineProps={({ lineNumber, totalLineCount }) => ({
                className:
                  lineNumber === 1
                    ? 'pt-2'
                    : lineNumber === totalLineCount
                      ? 'pb-2'
                      : '',
              })}
            />
          )
        ) : (
          <div className="p-10 text-center">
            <div className="pb-6">
              {isUnchanged
                ? 'There were no changes to this schema'
                : 'Click to view the full SDL'}
            </div>
            <Button variant="secondary" onClick={() => setShowSdl(true)}>
              View SDL
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
